import { useCallback, useRef, useState } from 'react';

import { FRONT_WARNING } from '@app/constants';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

import { stopCardInteraction } from '../utils';

import type { MouseEvent, MutableRefObject } from 'react';

export const PHONE_COPIED_RESET_MS = 2000;

interface UseAppointmentCardInfoTooltipProps {
  phone?: string;
  canShowContacts: boolean;
}

interface UseAppointmentCardInfoTooltipResult {
  isPhoneCopyable: boolean;
  isPhoneCopied: boolean;
  resetTimerRef: MutableRefObject<number | undefined>;
  handleCopyPhone(event: MouseEvent<HTMLElement>): Promise<void>;
}

export const useAppointmentCardInfoTooltip = ({
  phone,
  canShowContacts,
}: UseAppointmentCardInfoTooltipProps): UseAppointmentCardInfoTooltipResult => {
  const { showNotification } = useNotification();
  const [isPhoneCopied, setIsPhoneCopied] = useState(false);
  const resetTimerRef = useRef<number>();

  // Без права на контакты приходит маска «+**********» — её не копируем.
  const isPhoneCopyable = canShowContacts && Boolean(phone);

  const handleCopyPhone = useCallback(
    async (event: MouseEvent<HTMLElement>) => {
      stopCardInteraction(event);

      if (!isPhoneCopyable || !phone) {
        return;
      }

      try {
        await navigator.clipboard.writeText(phone);

        setIsPhoneCopied(true);
        window.clearTimeout(resetTimerRef.current);
        resetTimerRef.current = window.setTimeout(
          () => setIsPhoneCopied(false),
          PHONE_COPIED_RESET_MS
        );
      } catch {
        showNotification(
          'Не удалось скопировать номер телефона. Попробуйте ещё раз или наберите номер вручную.',
          FRONT_WARNING
        );
      }
    },
    [isPhoneCopyable, phone, showNotification]
  );

  return { isPhoneCopyable, isPhoneCopied, resetTimerRef, handleCopyPhone };
};
